import { useState } from "react";

interface Props {
  onSuccess: () => void;
  onLoginNeeded: () => void;
  isLoggedIn: boolean;
  buyerName: string;
}

const CATEGORIES = ["Electronics", "Apparel", "Machinery", "Auto Parts", "Health & Beauty", "Food & Beverage", "Chemicals", "Building Materials", "Safety Equipment", "Sports & Fitness", "Toys", "Vehicles", "Energy", "Home & Garden"];

const COUNTRIES = ["USA", "UK", "Canada", "Australia", "Germany", "France", "Japan", "South Korea", "UAE", "Saudi Arabia", "India", "Brazil", "Nigeria", "South Africa", "Singapore", "Netherlands", "Italy", "Spain", "Mexico", "Thailand", "Vietnam", "Indonesia", "Turkey", "Poland", "Sweden", "Norway", "Other"];

type Step = 1 | 2 | 3;

export default function PostRequirementPanel({ onSuccess, onLoginNeeded, isLoggedIn, buyerName }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [charCount, setCharCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    title: "",
    category: "",
    quantity: "",
    unit: "pcs",
    budgetMin: "",
    budgetMax: "",
    currency: "USD",
    deadline: "",
    country: "",
    company: "",
    email: "",
    whatsapp: "",
    description: "",
    sampleRequired: "no",
    shippingMethod: "sea",
    certifications: "",
  });

  const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  const step1Valid = form.title && form.category && form.quantity;
  const step2Valid = form.country && form.email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) { onLoginNeeded(); return; }
    if (charCount > 500) return;
    setSubmitting(true);
    const body = new URLSearchParams({
      ...form,
      budget: `${form.currency} ${form.budgetMin}${form.budgetMax ? " – " + form.budgetMax : ""}`,
      buyerName,
    });
    try {
      await fetch("https://readdy.ai/api/form/d7cmj5nsch5bvrijf6fg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch (_) { /* silent */ }
    setSubmitting(false);
    onSuccess();
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
            <i className="ri-file-add-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-white font-extrabold text-lg">Post Your Requirement</h2>
            <p className="text-white/80 text-xs">100% Free · Receive quotes from 20 Million+ verified factories</p>
          </div>
          <div className="ml-auto">
            <span className="bg-green-400/30 border border-green-300/40 text-white text-xs font-bold px-3 py-1.5 rounded-full">
              <i className="ri-checkbox-circle-fill mr-1"></i>FREE
            </span>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mt-4">
          {([1, 2, 3] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold transition-all ${step >= s ? "bg-white text-amber-500" : "bg-white/20 text-white/60"}`}>
                {step > s ? <i className="ri-check-line"></i> : s}
              </div>
              <span className={`text-xs font-semibold ${step >= s ? "text-white" : "text-white/50"}`}>
                {s === 1 ? "Product Info" : s === 2 ? "Your Details" : "Review & Submit"}
              </span>
              {i < 2 && <div className={`flex-1 h-px w-8 ${step > s ? "bg-white/60" : "bg-white/20"}`}></div>}
            </div>
          ))}
        </div>
      </div>

      <form data-readdy-form onSubmit={handleSubmit} className="p-6">

        {/* ── Step 1: Product Info ── */}
        {step === 1 && (
          <div className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                Product / Service Needed <span className="text-red-400">*</span>
              </label>
              <input
                name="title"
                value={form.title}
                onChange={e => set("title", e.target.value)}
                placeholder="e.g. 10,000 units Wireless Bluetooth Earbuds TWS"
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                  Category <span className="text-red-400">*</span>
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={e => set("category", e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                  required
                >
                  <option value="">Select category...</option>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                  Quantity <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    name="quantity"
                    value={form.quantity}
                    onChange={e => set("quantity", e.target.value)}
                    placeholder="e.g. 5,000"
                    className="flex-1 border border-stone-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    required
                  />
                  <select
                    name="unit"
                    value={form.unit}
                    onChange={e => set("unit", e.target.value)}
                    className="w-20 border border-stone-200 rounded-xl px-2 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                  >
                    {["pcs", "sets", "pairs", "kg", "tons", "rolls", "boxes", "units"].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1.5 block">Budget Range</label>
              <div className="flex items-center gap-2">
                <select
                  name="currency"
                  value={form.currency}
                  onChange={e => set("currency", e.target.value)}
                  className="w-20 border border-stone-200 rounded-xl px-2 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                >
                  {["USD", "EUR", "GBP", "CAD", "AUD", "AED"].map(c => <option key={c}>{c}</option>)}
                </select>
                <input
                  name="budgetMin"
                  value={form.budgetMin}
                  onChange={e => set("budgetMin", e.target.value)}
                  placeholder="Min (e.g. 10,000)"
                  className="flex-1 border border-stone-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
                <span className="text-stone-400 text-sm font-semibold">–</span>
                <input
                  name="budgetMax"
                  value={form.budgetMax}
                  onChange={e => set("budgetMax", e.target.value)}
                  placeholder="Max (e.g. 25,000)"
                  className="flex-1 border border-stone-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">Delivery Deadline</label>
                <input
                  name="deadline"
                  type="date"
                  value={form.deadline}
                  onChange={e => set("deadline", e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">Preferred Shipping</label>
                <select
                  name="shippingMethod"
                  value={form.shippingMethod}
                  onChange={e => set("shippingMethod", e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                >
                  <option value="sea">Sea Freight</option>
                  <option value="air">Air Freight</option>
                  <option value="express">Express Courier</option>
                  <option value="any">Any Method</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">Sample Required?</label>
                <div className="flex gap-2">
                  {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "maybe", l: "Maybe" }].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set("sampleRequired", opt.v)}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${form.sampleRequired === opt.v ? "border-amber-400 bg-amber-50 text-amber-700" : "border-stone-200 text-stone-500 hover:border-amber-200"}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">Certifications Needed</label>
                <input
                  name="certifications"
                  value={form.certifications}
                  onChange={e => set("certifications", e.target.value)}
                  placeholder="e.g. CE, RoHS, ISO 9001"
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                Detailed Description <span className="text-stone-400 font-normal">({charCount}/500)</span>
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={e => {
                  if (e.target.value.length <= 500) {
                    set("description", e.target.value);
                    setCharCount(e.target.value.length);
                  }
                }}
                rows={4}
                placeholder="Describe your product requirements in detail — specifications, materials, colors, packaging, OEM/ODM needs, etc."
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 resize-none transition-colors"
              />
              {charCount >= 500 && <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>}
            </div>

            <button
              type="button"
              disabled={!step1Valid}
              onClick={() => setStep(2)}
              className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Continue — Add Your Details <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        )}

        {/* ── Step 2: Buyer Details ── */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                  Your Country <span className="text-red-400">*</span>
                </label>
                <select
                  name="country"
                  value={form.country}
                  onChange={e => set("country", e.target.value)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                  required
                >
                  <option value="">Select country...</option>
                  {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 mb-1.5 block">Company / Business</label>
                <input
                  name="company"
                  value={form.company}
                  onChange={e => set("company", e.target.value)}
                  placeholder="Your company name"
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                Email Address <span className="text-red-400">*</span>
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={e => set("email", e.target.value)}
                placeholder="your@email.com — factories will contact you here"
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1.5 block">
                WhatsApp Number <span className="text-stone-400 font-normal">(optional but recommended)</span>
              </label>
              <div className="flex gap-2">
                <select className="w-24 border border-stone-200 rounded-xl px-2 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                  {["+1", "+44", "+49", "+33", "+61", "+81", "+82", "+91", "+86", "+55", "+971", "+234"].map(c => <option key={c}>{c}</option>)}
                </select>
                <input
                  name="whatsapp"
                  type="tel"
                  value={form.whatsapp}
                  onChange={e => set("whatsapp", e.target.value)}
                  placeholder="WhatsApp number"
                  className="flex-1 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <p className="text-xs text-stone-400 mt-1">Factories can reach you faster via WhatsApp</p>
            </div>

            {/* Summary preview */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-bold text-amber-700 mb-2">Your Requirement Summary</p>
              <div className="flex flex-col gap-1.5 text-xs text-stone-600">
                <div className="flex gap-2"><span className="text-stone-400 w-20 shrink-0">Product:</span><span className="font-semibold text-stone-800 truncate">{form.title || "—"}</span></div>
                <div className="flex gap-2"><span className="text-stone-400 w-20 shrink-0">Category:</span><span className="font-semibold text-stone-800">{form.category || "—"}</span></div>
                <div className="flex gap-2"><span className="text-stone-400 w-20 shrink-0">Quantity:</span><span className="font-semibold text-stone-800">{form.quantity ? `${form.quantity} ${form.unit}` : "—"}</span></div>
                <div className="flex gap-2"><span className="text-stone-400 w-20 shrink-0">Budget:</span><span className="font-semibold text-stone-800">{form.budgetMin ? `${form.currency} ${form.budgetMin}${form.budgetMax ? " – " + form.budgetMax : "+"}` : "—"}</span></div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 border border-stone-200 text-stone-600 font-semibold py-3 rounded-full hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-arrow-left-line mr-1"></i> Back
              </button>
              <button
                type="button"
                disabled={!step2Valid}
                onClick={() => setStep(3)}
                className="flex-1 bg-amber-400 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Review &amp; Submit <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review & Submit ── */}
        {step === 3 && (
          <div className="flex flex-col gap-4">
            <div className="bg-stone-50 rounded-2xl p-5">
              <p className="text-sm font-extrabold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-amber-400"></i> Final Review
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[
                  { label: "Product", value: form.title },
                  { label: "Category", value: form.category },
                  { label: "Quantity", value: `${form.quantity} ${form.unit}` },
                  { label: "Budget", value: form.budgetMin ? `${form.currency} ${form.budgetMin}${form.budgetMax ? " – " + form.budgetMax : "+"}` : "Not specified" },
                  { label: "Deadline", value: form.deadline || "Flexible" },
                  { label: "Shipping", value: form.shippingMethod === "sea" ? "Sea Freight" : form.shippingMethod === "air" ? "Air Freight" : form.shippingMethod === "express" ? "Express" : "Any Method" },
                  { label: "Sample", value: form.sampleRequired === "yes" ? "Required" : form.sampleRequired === "maybe" ? "Maybe" : "Not needed" },
                  { label: "Country", value: form.country },
                  { label: "Company", value: form.company || "Individual" },
                  { label: "Email", value: form.email },
                ].map(item => (
                  <div key={item.label} className="bg-white rounded-lg px-3 py-2">
                    <div className="text-stone-400 mb-0.5">{item.label}</div>
                    <div className="font-semibold text-stone-800 truncate">{item.value}</div>
                  </div>
                ))}
              </div>
              {form.description && (
                <div className="mt-3 bg-white rounded-lg px-3 py-2 text-xs">
                  <div className="text-stone-400 mb-0.5">Description</div>
                  <div className="text-stone-700 leading-relaxed">{form.description}</div>
                </div>
              )}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 text-lg shrink-0 mt-0.5"></i>
              <div>
                <p className="text-green-700 font-bold text-sm">Ready to post — completely free!</p>
                <p className="text-green-600 text-xs mt-0.5">Your requirement will be visible to all verified member factories. Expect quotes within 4 hours.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 border border-stone-200 text-stone-600 font-semibold py-3 rounded-full hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-arrow-left-line mr-1"></i> Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-white font-extrabold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Posting...
                  </span>
                ) : (
                  <><i className="ri-send-plane-fill mr-1.5"></i>Post Requirement — Free</>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
